// src/pages/ProfessionalResume.js
import React, { useState, useRef, useEffect } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import logo from "../../Images/LOGO512.png";

// Templates
const templates = [
  { id: 1, name: "Classic", color: "cyan-700", font: "sans", image: "/images/template1.png", type: "free" },
  { id: 2, name: "Modern", color: "blue-700", font: "serif", image: "/images/template2.png", type: "free" },
  { id: 3, name: "Professional", color: "gray-800", font: "sans", image: "/images/template3.png", type: "paid" },
  { id: 4, name: "Elegant", color: "purple-700", font: "serif", image: "/images/template4.png", type: "paid" },
  { id: 5, name: "Creative", color: "pink-600", font: "sans", image: "/images/template5.png", type: "free" },
];

// Default section order
const defaultSections = [
  "summary", "education", "experience", "skills", "projects", "certifications"
];

const Section = ({ title, color, children, visible, toggle }) => {
  if (!visible) return null;
  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-1">
        <h3 className={`text-sm md:text-lg font-semibold text-${color}`}>
          {title}
        </h3>
        <button
          onClick={toggle}
          className="text-gray-500 dark:text-gray-300 text-xs md:text-sm hover:text-gray-700 dark:hover:text-white"
        >
          Hide
        </button>
      </div>
      <p className="text-gray-800 dark:text-gray-100 text-sm md:text-base">{children}</p>
    </div>
  );
};

export default function ProfessionalResume() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    summary: "",
    education: "",
    experience: "",
    skills: "",
    projects: "",
    certifications: "",
  });
  const [selectedTemplate, setSelectedTemplate] = useState(templates[0]);
  const [sectionOrder, setSectionOrder] = useState(defaultSections);
  const [sectionVisibility, setSectionVisibility] = useState(
    defaultSections.reduce((acc, sec) => ({ ...acc, [sec]: true }), {})
  );
  const [filterType, setFilterType] = useState("all");
  const resumeRef = useRef();

  useEffect(() => {
    document.title = "Professional Resume Builder – Omkar More";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        "content",
        "Professional resume builder online. Choose templates, edit your info, customize sections, and download PDF instantly."
      );
    }
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const toggleSection = (sec) =>
    setSectionVisibility((prev) => ({ ...prev, [sec]: !prev[sec] }));

  const generatePDF = async () => {
    if (!resumeRef.current) return;
    const canvas = await html2canvas(resumeRef.current, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "pt", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save("resume.pdf");
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const newOrder = Array.from(sectionOrder);
    const [removed] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, removed);
    setSectionOrder(newOrder);
  };

  const formFields = [
    { name: "name", label: "Full Name" },
    { name: "email", label: "Email" },
    { name: "phone", label: "Phone" },
    { name: "summary", label: "Professional Summary" },
    { name: "education", label: "Education" },
    { name: "experience", label: "Experience" },
    { name: "skills", label: "Skills" },
    { name: "projects", label: "Projects" },
    { name: "certifications", label: "Certifications" },
  ];

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-blue-950">
      {/* Navbar */}
      <nav className="flex flex-col md:flex-row items-center justify-between bg-white dark:bg-blue-900 px-4 md:px-16 py-4 shadow-md sticky top-0 z-50">
        <div className="flex items-center gap-3 mb-2 md:mb-0">
          <img src={logo} alt="Logo" className="w-10 h-10 object-contain" />
          <span className="font-bold text-lg md:text-2xl text-cyan-700 dark:text-white">
            Omkar More Resume Builder
          </span>
        </div>
        <ul className="hidden md:flex gap-6 text-gray-800 dark:text-gray-200 font-medium">
          <li>Templates</li>
          <li>Free</li>
          <li>Paid</li>
          <li>Customize</li>
          <li>Preview</li>
        </ul>
      </nav>

      <div className="flex flex-col md:flex-row gap-6 px-4 md:px-16 py-8">
        {/* Left Toolbar */}
        <aside className="md:w-64 flex-shrink-0 bg-white dark:bg-blue-900 rounded-2xl shadow p-4 sticky top-24 h-max">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">Customize Sections</h3>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="sections">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                  {sectionOrder.map((section, index) => (
                    <Draggable key={section} draggableId={section} index={index}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className="p-2 bg-gray-200 dark:bg-blue-950 rounded-lg flex justify-between items-center cursor-move text-gray-900 dark:text-gray-100"
                        >
                          {section.charAt(0).toUpperCase() + section.slice(1)}
                          <button onClick={() => toggleSection(section)} className="text-xs text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white">
                            {sectionVisibility[section] ? "Hide" : "Show"}
                          </button>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <div className="mt-6">
            <h4 className="font-semibold mb-2">Filter Templates</h4>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-700 rounded-lg p-2 bg-gray-50 dark:bg-blue-950 text-gray-900 dark:text-gray-100"
            >
              <option value="all">All</option>
              <option value="free">Free</option>
              <option value="paid">Paid</option>
            </select>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 flex flex-col gap-6">
          {/* Template Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {templates
              .filter((t) => filterType === "all" || t.type === filterType)
              .map((temp) => (
                <div
                  key={temp.id}
                  onClick={() => setSelectedTemplate(temp)}
                  className={`cursor-pointer rounded-2xl overflow-hidden shadow-md transform transition hover:scale-105 border-2 ${
                    selectedTemplate.id === temp.id ? "border-cyan-700" : "border-transparent"
                  }`}
                >
                  <img src={temp.image} alt={temp.name} className="w-full h-32 object-cover" />
                  <h4 className="text-center p-2 text-gray-900 dark:text-gray-100 font-medium">{temp.name}</h4>
                </div>
              ))}
          </div>

          <div className="flex flex-col md:flex-row gap-6">
            {/* Form */}
            <div className="flex-1 bg-white dark:bg-blue-900 p-6 rounded-2xl shadow-xl space-y-4">
              {formFields.map((field) => (
                <div key={field.name}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">{field.label}</label>
                  <textarea
                    name={field.name}
                    value={form[field.name]}
                    onChange={handleChange}
                    rows={["summary","experience","education","projects"].includes(field.name) ? 3 : 1}
                    className="w-full border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 bg-gray-50 dark:bg-blue-950 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:outline-none transition-colors duration-200"
                  />
                </div>
              ))}
            </div>

            {/* Resume Preview */}
            <div className="flex-1 bg-white dark:bg-blue-950 p-6 rounded-2xl shadow-xl overflow-x-auto">
              <div
                ref={resumeRef}
                className={`p-6 border-l-4 border-${selectedTemplate.color} rounded-lg bg-white dark:bg-blue-950 min-w-[300px]`}
              >
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
                  <h2 className={`text-xl md:text-3xl font-bold ${selectedTemplate.font === "serif" ? "font-serif" : "font-sans"} text-${selectedTemplate.color} mb-2 md:mb-0`}>
                    {form.name || "Your Name"}
                  </h2>
                  <div className="text-sm text-gray-700 dark:text-gray-200 text-left md:text-right">
                    <p>{form.email || "Email"}</p>
                    <p>{form.phone || "Phone"}</p>
                  </div>
                </div>

                {sectionOrder.map((section) =>
                  form[section] ? (
                    <Section
                      key={section}
                      title={section.charAt(0).toUpperCase() + section.slice(1)}
                      color={selectedTemplate.color}
                      visible={sectionVisibility[section]}
                      toggle={() => toggleSection(section)}
                    >
                      {form[section]}
                    </Section>
                  ) : null
                )}
              </div>

              {/* Download */}
              <div className="mt-4 text-center">
                <button
                  onClick={generatePDF}
                  className="px-6 py-2 bg-cyan-700 text-white rounded-full font-semibold hover:bg-cyan-600 transition"
                >
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
